import React, { useState, useEffect } from 'react';
import financialService from '../../services/financialService';
import AdvancedDataTable from '../ui/AdvancedDataTable';
import './FinancialDashboard.css';

const DimensionAnalysis = ({ marketplace, cacheKey, horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones' }) => {
  const [loading, setLoading] = useState(true);
  const [dimension, setDimension] = useState('proveedor');
  const [limit, setLimit] = useState(10);
  const [aggregatedData, setAggregatedData] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    loadAnalysis();
  }, [marketplace, cacheKey, dimension, limit, horizonteTemporal, tipoVolumen]);
  
  const loadAnalysis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await financialService.getDimensionAnalysis(
        marketplace, 
        cacheKey, 
        dimension, 
        limit,
        horizonteTemporal,
        tipoVolumen
      );
      
      if (result.success) {
        setAggregatedData(result.aggregated_data || []);
        setTotalGroups(result.total_groups || 0);
      }
    } catch (err) {
      console.error('Error cargando análisis por dimensión:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-';
    const num = typeof val === 'number' ? val : parseFloat(val);
    if (isNaN(num)) return '-';
    return `$${num.toFixed(2)}`;
  };
  
  const formatPercent = (val) => {
    if (val === null || val === undefined) return '-';
    const num = typeof val === 'number' ? val : parseFloat(val);
    if (isNaN(num)) return '-';
    return `${num.toFixed(2)}%`;
  };
  
  const columns = [
    { 
      field: dimension, 
      headerName: dimension.charAt(0).toUpperCase() + dimension.slice(1), 
      width: 200, 
      minWidth: 150 
    },
    { 
      field: 'Ingresos Total', 
      headerName: 'Ingresos Total', 
      valueFormatter: (params) => formatCurrency(params.value), 
      width: 150 
    },
    { 
      field: 'Precio Promedio', 
      headerName: 'Precio Promedio', 
      valueFormatter: (params) => formatCurrency(params.value), 
      width: 150 
    },
    { 
      field: 'Publicaciones', 
      headerName: 'Publicaciones', 
      width: 120 
    },
    { 
      field: 'Costo Total', 
      headerName: 'Costo Total', 
      valueFormatter: (params) => formatCurrency(params.value), 
      width: 150 
    },
    { 
      field: 'Utilidad Total', 
      headerName: 'Utilidad Total', 
      valueFormatter: (params) => formatCurrency(params.value), 
      width: 150 
    },
    { 
      field: 'Utilidad Promedio', 
      headerName: 'Utilidad Promedio', 
      valueFormatter: (params) => formatCurrency(params.value), 
      width: 150 
    },
    { 
      field: 'Margen %', 
      headerName: 'Margen %', 
      valueFormatter: (params) => formatPercent(params.value), 
      width: 120 
    }
  ];
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando análisis por {dimension}...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <p>❌ {error}</p>
        <button onClick={loadAnalysis} className="btn btn-primary">
          🔄 Reintentar
        </button>
      </div>
    );
  }
  
  return (
    <div className="dimension-analysis">
      <div className="analysis-header">
        <h3>🔍 Análisis por Dimensión</h3>
        <p className="analysis-description">
          Análisis agregado mostrando métricas financieras agrupadas por {dimension}.
          Mostrando top {limit} de {totalGroups} grupos.
        </p>
      </div>
      
      <div className="analysis-controls">
        <div className="control-item">
          <label>Dimensión de análisis:</label>
          <select 
            value={dimension} 
            onChange={(e) => setDimension(e.target.value)}
            className="dimension-select"
          >
            <option value="proveedor">Proveedor</option>
            <option value="sku">SKU</option>
            <option value="identificador">Identificador</option>
          </select>
        </div>
        
        <div className="control-item">
          <label>Mostrar top:</label>
          <select 
            value={limit} 
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="limit-select"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>
      
      <div className="table-container">
        {aggregatedData.length > 0 ? (
          <AdvancedDataTable 
            data={aggregatedData}
            columns={columns}
            pageSize={limit}
            enableSearch={true}
            enableSort={true}
          />
        ) : (
          <div className="no-data">
            No hay datos agregados disponibles
          </div>
        )}
      </div>
      
      {aggregatedData.length > 0 && totalGroups > limit && (
        <div className="showing-info">
          <p>
            Mostrando {Math.min(limit, aggregatedData.length)} de {totalGroups} grupos totales.
            Aumente el límite para ver más resultados.
          </p>
        </div>
      )}
    </div>
  );
};

export default DimensionAnalysis;

